import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { NumericFormat, NumberFormatValues } from "react-number-format"
import {
  isNoOpAfterNormalization,
  normalizeNumericInputValue,
} from "utils/decimalInput"
import {
  DetailsTrailing,
  LabelBox,
  PercentLabel,
  StyledInput,
  StyledLabel,
  StyledTextFieldFormItem,
} from "./styled"

interface IProps {
  label: string
  percent: null | number
  value: null | number
  onChange?: (value: null | number) => void
  placeholder?: string
  detailTrailing?: string
  isError?: boolean
  disabled?: boolean
  onFocus?: () => void
}

const MacroGramInput = (props: IProps) => {
  const {
    detailTrailing,
    isError = false,
    disabled = false,
    onFocus,
    onChange,
    placeholder,
    value,
    label,
    percent,
  } = props

  const [internalValue, setInternalValue] = useState(value?.toString() ?? "")

  useEffect(() => {
    const hasNumberValue = value !== null && !isNaN(value)
    const isValueChanged =
      value !== Number(internalValue) || Number(internalValue) === 0

    if (hasNumberValue && isValueChanged) {
      setInternalValue(value.toString())
    }
  }, [value])

  const inputRef = useRef<HTMLInputElement>(null)

  const onValueChange = (values: NumberFormatValues) => {
    if (disabled) return

    const normalized = normalizeNumericInputValue(values.value)

    if (normalized === "") {
      setInternalValue("")
      onChange?.(null)
      return
    }

    setInternalValue(normalized)

    if (values.floatValue !== undefined && values.floatValue !== value) {
      onChange?.(values.floatValue)
    }
  }

  const isAllowed = (values: NumberFormatValues) => {
    const normalized = normalizeNumericInputValue(values.value)
    return !isNoOpAfterNormalization(values.value, normalized, internalValue)
  }

  const trailingRef = useRef<HTMLDivElement>(null)
  const [trailingWidth, setTrailWidth] = useState(0)

  useLayoutEffect(() => {
    setTrailWidth(trailingRef.current?.getBoundingClientRect().width ?? 0)
  }, [trailingRef, detailTrailing])

  return (
    <StyledTextFieldFormItem
      style={
        {
          "--input-padding-right": "var(--input-padding)",
          "--input-trailing-padding-left": detailTrailing ? "2px" : "0px",
          "--detail-trailing-width": `${trailingWidth}px`,
        } as React.CSSProperties
      }
    >
      <LabelBox>
        <StyledLabel>{label}</StyledLabel>
        <PercentLabel>{percent ?? 0}%</PercentLabel>
      </LabelBox>

      <NumericFormat
        isAllowed={isAllowed}
        customInput={StyledInput}
        getInputRef={inputRef}
        inputMode="numeric"
        decimalScale={0}
        allowNegative={false}
        thousandSeparator=","
        $disabled={disabled}
        disabled={disabled}
        onFocus={onFocus}
        $isError={isError}
        placeholder={placeholder}
        value={internalValue ?? ""}
        valueIsNumericString
        onValueChange={onValueChange}
        onBlur={() => {
          if (internalValue === "") {
            onChange?.(0)
          }
        }}
        autoComplete="off"
        style={{
          paddingRight:
            "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width))",
        }}
      />
      {detailTrailing && <DetailsTrailing>{detailTrailing}</DetailsTrailing>}

      {detailTrailing && (
        <DetailsTrailing style={{ visibility: "hidden" }} ref={trailingRef}>
          {detailTrailing}
        </DetailsTrailing>
      )}
    </StyledTextFieldFormItem>
  )
}

export default MacroGramInput
